import { useEffect, useState } from "react";
import { DownloadSimple, WifiSlash, DeviceMobile, CloudArrowUp, ArrowClockwise, Warning, Queue } from "@phosphor-icons/react";
import { useLang } from "@/i18n";
import { subscribeInstall, promptInstall } from "@/lib/pwa";
import { api, subscribeQueue, drainQueue } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SyncQueueDrawer from "@/components/SyncQueueDrawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function PWAControls() {
  const { t } = useLang();
  const [canInstall, setCanInstall] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const standalone = isStandalone();
  const iosDevice = isIos();

  useEffect(() => {
    const unsubInstall = subscribeInstall(({ canInstall }) => setCanInstall(canInstall));
    const unsubQueue = subscribeQueue((s) => {
      setPending(s.pending || 0);
      setFailedCount(s.failed || 0);
    });
    const onOnline = () => { setOnline(true); toast.success(t("backOnline")); };
    const onOffline = () => { setOnline(false); toast.warning(t("wentOffline")); };
    const onSynced = (e) => {
      const { synced = 0, failed = 0 } = e.detail || {};
      if (synced > 0) {
        toast.success(`${synced} ${t("changesSynced")}`, {
          action: { label: t("reload"), onClick: () => window.location.reload() },
          duration: 8000,
        });
      }
      if (failed > 0) {
        toast.error(`${failed} ${t("changesFailed")}`, {
          action: { label: t("review"), onClick: () => setDrawerOpen(true) },
          duration: 10000,
        });
      }
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-synced", onSynced);
    return () => {
      unsubInstall();
      unsubQueue();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-synced", onSynced);
    };
  }, [t]);

  const handleInstall = async () => {
    if (iosDevice && !standalone) { setShowIosGuide(true); return; }
    if (!canInstall) { toast.info(t("installUnavailable")); return; }
    const res = await promptInstall();
    if (res?.outcome === "accepted") toast.success(t("installSuccess"));
  };

  const handleSyncNow = async () => {
    if (!navigator.onLine) { toast.warning(t("wentOffline")); return; }
    setSyncing(true);
    try {
      const result = await drainQueue(api);
      if (result.synced > 0) {
        toast.success(`${result.synced} ${t("changesSynced")}`, {
          action: { label: t("reload"), onClick: () => window.location.reload() },
          duration: 8000,
        });
      } else if (result.remaining > 0) {
        toast.warning(t("syncPartial"));
      } else {
        toast.info(t("nothingToSync"));
      }
    } finally {
      setSyncing(false);
    }
  };

  const showInstallButton = !standalone && (canInstall || iosDevice);

  return (
    <>
      {!online && (
        <span
          data-testid="offline-indicator"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
        >
          <WifiSlash size={14} weight="bold" /> {t("offlineMode")}
        </span>
      )}
      {pending > 0 && (
        <button
          data-testid="pending-sync-badge"
          onClick={handleSyncNow}
          disabled={syncing}
          title={t("pendingChangesHint")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-60"
        >
          {syncing ? <ArrowClockwise size={14} weight="bold" className="animate-spin" /> : <CloudArrowUp size={14} weight="bold" />}
          <span data-testid="pending-count">{pending}</span> {t("pendingChanges")}
        </button>
      )}
      {failedCount > 0 && (
        <button
          data-testid="failed-sync-badge"
          onClick={() => setDrawerOpen(true)}
          title={t("failedChangesHint")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-800 border border-red-200 hover:bg-red-100 transition-colors"
        >
          <Warning size={14} weight="fill" />
          <span data-testid="failed-count">{failedCount}</span> {t("failedChanges")}
        </button>
      )}
      {(pending > 0 || failedCount > 0) && (
        <button
          data-testid="sync-queue-open"
          onClick={() => setDrawerOpen(true)}
          title={t("openSyncQueue")}
          className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
        >
          <Queue size={14} />
        </button>
      )}
      <SyncQueueDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      {showInstallButton && (
        <Button
          size="sm"
          variant="outline"
          data-testid="install-app-button"
          onClick={handleInstall}
          className="gap-1.5 h-8 border-blue-900 text-blue-900 hover:bg-blue-50"
        >
          <DownloadSimple size={14} weight="bold" />
          <span className="hidden sm:inline">{t("installApp")}</span>
        </Button>
      )}

      <Dialog open={showIosGuide} onOpenChange={setShowIosGuide}>
        <DialogContent data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DeviceMobile size={22} weight="duotone" /> {t("installOnIos")}
            </DialogTitle>
            <DialogDescription>{t("iosInstallHint")}</DialogDescription>
          </DialogHeader>
          <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
            <li>{t("iosStep1")}</li>
            <li>{t("iosStep2")}</li>
            <li>{t("iosStep3")}</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
